import { onMounted, onUnmounted, readonly, ref } from 'vue'
import { registerSW } from 'virtual:pwa-register'
import { track } from '../lib/analytics'

/**
 * Installasjon og versjonsoppdatering.
 *
 * Begge deler krever en service worker, og service workers kjører kun i en
 * «secure context»: HTTPS, eller localhost. På en ren IP-adresse over HTTP
 * skjer ingenting - da er både installasjonsforslaget og oppdaterings-
 * varselet stille av seg selv.
 */

const DISMISSED_KEY = 'bonto.install.dismissed'
// Etter et avslag lar vi det gå en måned før vi spør igjen.
const SNOOZE_DAYS = 30

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true

const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
  !/crios|fxios/i.test(window.navigator.userAgent)

function recentlyDismissed() {
  const stored = Number(localStorage.getItem(DISMISSED_KEY) || 0)
  return stored > 0 && Date.now() - stored < SNOOZE_DAYS * 86_400_000
}

// Chrome sender beforeinstallprompt tidlig - ofte før Vue har rukket å montere
// noe. Vi fanger den her, på modulnivå, så hendelsen ikke går tapt.
let deferredPrompt = null
const promptAvailable = ref(false)

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredPrompt = event
    promptAvailable.value = true
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    promptAvailable.value = false
    localStorage.removeItem(DISMISSED_KEY)
    track('pwa-installert')
  })

  // Kjører appen fra hjemskjermen? Det er den beste enkeltmålingen på om noen
  // har tatt appen i bruk for alvor, og finnes ikke noe annet sted - en
  // installert app ser ut som et helt vanlig besøk i tallene ellers.
  if (isStandalone()) track('apen-som-app')
}

// Service workeren registreres én gang for hele appen.
const needsRefresh = ref(false)
let applyUpdate = null

if (typeof window !== 'undefined') {
  applyUpdate = registerSW({
    onNeedRefresh() { needsRefresh.value = true },
    onRegisteredSW(_url, registration) {
      // Nettleseren ser ellers bare etter ny versjon ved oppstart. En app som
      // ligger åpen på en mobil i dagevis ville aldri oppdaget en utrulling.
      if (!registration) return
      setInterval(() => registration.update().catch(() => {}), 60 * 60 * 1000)

      // Sjekk også når appen hentes fram igjen.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') registration.update().catch(() => {})
      })
    },
  })
}

export function usePwa() {
  const standalone = ref(isStandalone())
  const canInstall = ref(false)
  const showIosHint = ref(false)

  function evaluate() {
    standalone.value = isStandalone()
    const eligible = !standalone.value && !recentlyDismissed()
    canInstall.value = eligible && promptAvailable.value
    // Safari har ingen installasjonsdialog - der må brukeren vise vei selv.
    showIosHint.value = eligible && isIos() && !promptAvailable.value
  }

  let stop = null
  onMounted(() => {
    evaluate()
    const media = window.matchMedia('(display-mode: standalone)')
    const onChange = () => evaluate()
    media.addEventListener('change', onChange)
    const unwatch = setInterval(evaluate, 2000)
    stop = () => { media.removeEventListener('change', onChange); clearInterval(unwatch) }
  })
  onUnmounted(() => stop?.())

  async function install() {
    if (!deferredPrompt) return false
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    deferredPrompt = null
    promptAvailable.value = false
    if (outcome === 'dismissed') dismiss()
    evaluate()
    return outcome === 'accepted'
  }

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()))
    canInstall.value = false
    showIosHint.value = false
  }

  /** Aktiverer den nye versjonen og laster siden på nytt. */
  function refresh() {
    needsRefresh.value = false
    applyUpdate?.(true)
  }

  return {
    canInstall: readonly(canInstall),
    showIosHint: readonly(showIosHint),
    isStandalone: readonly(standalone),
    needsRefresh: readonly(needsRefresh),
    install,
    dismiss,
    refresh,
  }
}
